// Line maps by blob pair (docs/design.md "Review marks"): where each line
// of an earlier blob of a file is in a later one, which is how marks on the
// earlier blob carry. A line map is a function of the two texts alone, so
// it is kept by the pair of blob IDs: in memory for the session, and in the
// signed-in account's IndexedDB (`kv` store, deleted at sign-out) so a
// reload neither fetches the earlier blob nor maps it again.
//
// Each write prunes records beyond the newest LINE_MAPS_KEPT, found through
// an index record (LINE_MAP_INDEX), as reviewStatus.ts does.
import { LruMap } from '../api/cache';
import { idbDelete, idbGet, idbPut } from '../api/db';
import { lineMapKey } from './model';

const PREFIX = 'line-map|';
/** A text ID's GitHub blob SHA, where they differ (few: kept without pruning). */
const ALIAS = 'blob-alias|';
/** The record listing every kept line map's key and write time. */
export const LINE_MAP_INDEX = 'line-map-index';
/** Line maps kept in IndexedDB at most, newest first. */
export const LINE_MAPS_KEPT = 500;

/** The key-value storage line maps are kept in. */
export interface LineMapKv {
  get(key: string): Promise<unknown>;
  put(key: string, value: unknown): Promise<unknown>;
  delete(key: string): Promise<unknown>;
}

export interface LineMapStore {
  read(from: string, to: string): Promise<number[] | undefined>;
  write(from: string, to: string, map: number[]): Promise<void>;
  /** GitHub's blob SHA of the text whose ID (`blobId`) is `id`, where they
   *  differ (a byte-order mark or invalid UTF-8 the decoding dropped). */
  readAlias(id: string): Promise<string | undefined>;
  writeAlias(id: string, sha: string): Promise<void>;
}

/** A line map store over `kv`, with a session cache in front. Reads and
 *  writes that fail (storage full or unavailable) act as misses. */
export function kvLineMapStore(kv: LineMapKv, now: () => number = Date.now): LineMapStore {
  const memory = new LruMap<string, number[]>(200);
  const aliases = new LruMap<string, string>(2000);
  return {
    async readAlias(id) {
      const hit = aliases.get(id);
      if (hit) return hit;
      try {
        const v = await kv.get(ALIAS + id);
        if (typeof v === 'string') {
          aliases.set(id, v);
          return v;
        }
      } catch {
        /* a miss */
      }
      return undefined;
    },
    async writeAlias(id, sha) {
      if (aliases.get(id) === sha) return;
      aliases.set(id, sha);
      try {
        await kv.put(ALIAS + id, sha);
      } catch {
        /* kept for the session only */
      }
    },
    async read(from, to) {
      const k = lineMapKey(from, to);
      const hit = memory.get(k);
      if (hit) return hit;
      try {
        const v = await kv.get(PREFIX + k);
        if (Array.isArray(v) && v.every((n) => typeof n === 'number')) {
          memory.set(k, v);
          return v;
        }
      } catch {
        /* a miss */
      }
      return undefined;
    },
    async write(from, to, map) {
      const k = lineMapKey(from, to);
      memory.set(k, map);
      try {
        await kv.put(PREFIX + k, map);
        const stored = await kv.get(LINE_MAP_INDEX);
        const index: Record<string, number> = stored && typeof stored === 'object' ? { ...(stored as Record<string, number>) } : {};
        index[k] = now();
        const keep = new Set(
          Object.entries(index)
            .sort(([, a], [, b]) => b - a)
            .slice(0, LINE_MAPS_KEPT)
            .map(([key]) => key),
        );
        for (const key of Object.keys(index)) {
          if (keep.has(key)) continue;
          delete index[key];
          await kv.delete(PREFIX + key);
        }
        await kv.put(LINE_MAP_INDEX, index);
      } catch {
        /* kept for the session only */
      }
    },
  };
}

export const idbLineMapStore: LineMapStore = kvLineMapStore({
  get: (k) => idbGet('kv', k),
  put: (k, v) => idbPut('kv', k, v),
  delete: (k) => idbDelete('kv', k),
});
